/**
 * Money at the edge of the UI only.
 *
 * The database, Stripe, `amount_paid` and the payments ledger all speak CENTS,
 * and that does not change — an admin typing "13500" to mean $135 was the
 * problem, not the storage unit. These two helpers are the single conversion
 * point so there is no second copy to drift out of step.
 */

/**
 * "135.10" -> 13510. Returns null for anything that is not a sane price.
 *
 * Null rather than 0 is deliberate: a caller that does `dollarsToCents(x) || 0`
 * on an empty field creates a live $0.00 price option, which is a real bug this
 * codebase has already carried. Callers must handle null as "don't save".
 */
export function dollarsToCents(value) {
  if (value === null || value === undefined) return null;
  const cleaned = String(value).trim().replace(/^\$/, '');
  if (cleaned === '') return null;
  // At most two decimal places. 135.005 is not a price, and rounding it
  // silently would hide the admin's typo rather than surface it.
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  // Math.round, not truncation: 135.10 * 100 is 13510.000000000002 in binary
  // floating point, so `| 0` or parseInt would give 13509.
  return Math.round(parseFloat(cleaned) * 100);
}

/** 13510 -> "135.10". Empty string for null/undefined so an input never shows NaN. */
export function centsToDollars(cents) {
  if (cents === null || cents === undefined) return '';
  const n = Number(cents);
  if (Number.isNaN(n)) return '';
  return (n / 100).toFixed(2);
}
